import type { DraftState, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { ControlCandidate, ControlDecision } from './negativeControls.ts'
import { labelNegativeControls } from './negativeControls.ts'
import { hasKeyColumn } from './sheet/sheetMapping.ts'
import { sheetMatchOf } from './sheet/sheetMatching.ts'
import { sampleStemAsWritten } from '../../Util/fileNames.ts'
import { plural } from '../../Util/plural.ts'
import { resolveItemRole } from '../../Util/sheetItemRoles/index.ts'
import { sheetTypesOf } from './sheet/sheetRoles.ts'

/** "1 negative control", "2 samples", "2 samples and 1 negative control". */
export const itemsByType = (samples: number, controls: number): string => {
  if (samples > 0 && controls > 0) {
    return `${plural(samples, 'sample')} and ${plural(controls, 'negative control')}`
  }
  return controls > 0 ? plural(controls, 'negative control') : plural(samples, 'sample')
}

export const countItems = (groups: readonly { itemType?: WizardSampleGroup['itemType'] }[]): string => {
  const controls = groups.filter((group) => group.itemType === 'NEGATIVE_CONTROL').length
  return itemsByType(groups.length - controls, controls)
}

/** The sample name a sheet row gives a group, if the sheet matches it and names it. */
export const sheetNameOf = (draft: DraftState, groupKey: string): string | undefined => {
  const sheet = draft.sheet
  if (!sheet?.mapping.sampleName || !hasKeyColumn(sheet.mapping)) {
    return undefined
  }
  const rowIndex = sheetMatchOf(draft)?.byGroup[groupKey]
  return rowIndex === undefined ? undefined : sheet.rows[rowIndex]?.[sheet.mapping.sampleName]?.trim() || undefined
}

/**
 * The sample name when no sheet names the sample: the forward file's name without the bcl2fastq tail
 * (`_S12_L001_R1_001`), a mate token (`_R1`, `_1`, `.fwd`, …) or its extensions. The classic flow sets no
 * default name, so this is the wizard's own rule; a sheet name always wins.
 */
export const fileSampleName = (draft: DraftState, group: WizardSampleGroup): string | undefined => {
  const name = draft.files[group.forward]?.name
  return name ? sampleStemAsWritten(name) : undefined
}

export const sampleNameOf = (draft: DraftState, group: WizardSampleGroup): string | undefined =>
  sheetNameOf(draft, group.key) ?? fileSampleName(draft, group)

/** Where the customer sample name written for a group comes from; `undefined` when none is written. */
export const sampleNameSource = (draft: DraftState, groupKey: string): 'sheet' | 'file' | undefined => {
  const group = draft.groups[groupKey]
  if (!group) {
    return undefined
  }
  if (sheetNameOf(draft, groupKey)) {
    return 'sheet'
  }
  return fileSampleName(draft, group) ? 'file' : undefined
}

/** A sample as the wizard names it to the lab: its sample name, else its group key. */
export const sampleDisplayName = (draft: DraftState, groupKey: string): string => {
  const group = draft.groups[groupKey]
  return (group && sampleNameOf(draft, group)) || groupKey
}

const candidates = (draft: DraftState): ControlCandidate[] =>
  Object.values(draft.groups).map((group) => ({
    key: group.key,
    fileName: draft.files[group.forward]?.name ?? '',
    sampleName: sheetNameOf(draft, group.key)
  }))

export const controlDecision = (draft: DraftState): { flagged: Set<string>; decision: ControlDecision } => {
  const { flagged, decision } = labelNegativeControls(candidates(draft))
  return { flagged: new Set(flagged), decision }
}

/**
 * Every group not set by the user gets its automatic label: the type the sheet states for it, else a
 * negative control when the names say so and the order-level policy (or the user's answer to its
 * question) allows labelling, a sample otherwise.
 */
export const applyControlLabels = (draft: DraftState): DraftState => {
  const groups = Object.values(draft.groups)
  if (groups.length === 0) {
    return draft
  }
  /* an archive's own item carries its samples; their labels live on the archive's sheet samples */
  const archive = draft.settings?.sampleConfiguration.value === 'COMBINED_SAMPLE_PAIRS'
  const { flagged, decision } = controlDecision(draft)
  const label = !archive && draft.controlsAnswer !== 'none' && (decision === 'auto' || draft.controlsAnswer === 'accept')
  const sheetTypes = sheetTypesOf(draft)
  let changed = false
  const next: Record<string, WizardSampleGroup> = {}
  for (const group of groups) {
    const resolved = resolveItemRole({
      manual: group.typeSource === 'user' ? group.itemType : undefined,
      sheet: sheetTypes[group.key],
      name: label && flagged.has(group.key) ? 'NEGATIVE_CONTROL' : 'SAMPLE'
    })
    if (!resolved || resolved.source === 'manual') {
      next[group.key] = group
      continue
    }
    const typeSource = resolved.source === 'sheet' ? 'sheet' : 'auto'
    changed ||= group.itemType !== resolved.role || group.typeSource !== typeSource
    next[group.key] = { ...group, itemType: resolved.role, typeSource }
  }
  return changed ? { ...draft, groups: next } : draft
}

/**
 * Groups whose sheet type contradicts their names, once the names recognise controls at all: shown as a
 * note, since the sheet wins and the lab can still mark any item by hand.
 */
export const sheetTypeDisagreements = (draft: DraftState): string[] => {
  const sheetTypes = sheetTypesOf(draft)
  const { flagged, decision } = controlDecision(draft)
  if (decision === 'none-found') {
    return []
  }
  return Object.entries(sheetTypes)
    .filter(([key, itemType]) => (itemType === 'NEGATIVE_CONTROL') !== flagged.has(key))
    .map(([key]) => key)
}
